import type { JourneyMode } from "@/data/schema/compiled";

export const TIMELINE_UI_STORAGE_KEY = "timeline-ui-v1";

export const TRANSPORT_SIZE_MIN = 1;
export const TRANSPORT_SIZE_MAX = 5;
export const DEFAULT_TRANSPORT_SIZE_LEVEL = 1;

export interface TransportPosition {
  /** 0–1, horizontal center of the control bar in the stage */
  x: number;
  /** 0–1, vertical center of the control bar in the stage */
  y: number;
}

/**
 * Stored center of the bar. The default is drawn docked at the bottom left
 * so the bar stays on screen; a drag switches to this center fraction.
 */
export const DEFAULT_TRANSPORT_POSITION: TransportPosition = {
  x: 0.14,
  y: 0.92,
};

/** Earlier default (bottom center). Sessions still on it move to the new spot. */
const LEGACY_TRANSPORT_POSITION: TransportPosition = {
  x: 0.5,
  y: 0.92,
};

/**
 * off: no spoilers anywhere. enabled: a closed heading on scenes that have them.
 * shown: headings open, later names on labels, and hidden items on the rail.
 */
export type SpoilerLevel = "off" | "enabled" | "shown";

/** The old on/off setting meant everything was shown. */
function readSpoilerLevel(
  parsed: { spoilerLevel?: unknown; spoilersEnabled?: unknown },
): SpoilerLevel {
  if (parsed.spoilerLevel === "enabled" || parsed.spoilerLevel === "shown") {
    return parsed.spoilerLevel;
  }
  return parsed.spoilerLevel === undefined && parsed.spoilersEnabled === true ? "shown" : "off";
}

export interface TimelineUiPreferences {
  transportSizeLevel: number;
  transportPosition: TransportPosition;
  cameraLockEnabled: boolean;
  /** Locked camera eases zoom so the cast and their locations stay in frame. */
  autoZoomEnabled: boolean;
  boardModelDebug: boolean;
  journey: JourneyMode;
  /** Chapter dropdown choice. Null until one is made. */
  spanId: string | null;
  repeatEnabled: boolean;
  optionsOpen: boolean;
  /** Later-chapter context in earlier scenes. Off until asked for. */
  spoilerLevel: SpoilerLevel;
}

const DEFAULT_PREFERENCES: TimelineUiPreferences = {
  transportSizeLevel: DEFAULT_TRANSPORT_SIZE_LEVEL,
  transportPosition: DEFAULT_TRANSPORT_POSITION,
  cameraLockEnabled: true,
  autoZoomEnabled: true,
  boardModelDebug: false,
  journey: "book",
  spanId: null,
  repeatEnabled: false,
  optionsOpen: false,
  spoilerLevel: "off",
};

export function clampTransportSizeLevel(level: number): number {
  return Math.min(
    TRANSPORT_SIZE_MAX,
    Math.max(TRANSPORT_SIZE_MIN, Math.round(level)),
  );
}

export function clampTransportPosition(
  position: TransportPosition,
): TransportPosition {
  return {
    x: Math.min(1, Math.max(0, position.x)),
    y: Math.min(1, Math.max(0, position.y)),
  };
}

function parseTransportPosition(
  value: unknown,
): TransportPosition {
  if (
    typeof value === "object" &&
    value !== null &&
    typeof (value as TransportPosition).x === "number" &&
    typeof (value as TransportPosition).y === "number"
  ) {
    const position = clampTransportPosition(value as TransportPosition);
    if (isLegacyTransportPosition(position)) {
      return DEFAULT_TRANSPORT_POSITION;
    }
    return position;
  }
  return DEFAULT_TRANSPORT_POSITION;
}

export function isDefaultTransportPosition(
  position: TransportPosition,
): boolean {
  return (
    Math.abs(position.x - DEFAULT_TRANSPORT_POSITION.x) < 0.001 &&
    Math.abs(position.y - DEFAULT_TRANSPORT_POSITION.y) < 0.001
  );
}

function isLegacyTransportPosition(position: TransportPosition): boolean {
  return (
    Math.abs(position.x - LEGACY_TRANSPORT_POSITION.x) < 0.001 &&
    Math.abs(position.y - LEGACY_TRANSPORT_POSITION.y) < 0.001
  );
}

export function readTimelineUiPreferences(): TimelineUiPreferences {
  if (typeof localStorage === "undefined") {
    return DEFAULT_PREFERENCES;
  }
  try {
    const raw = localStorage.getItem(TIMELINE_UI_STORAGE_KEY);
    if (raw === null) {
      return DEFAULT_PREFERENCES;
    }
    const parsed = JSON.parse(raw) as Partial<TimelineUiPreferences>;
    return {
      transportSizeLevel: clampTransportSizeLevel(
        parsed.transportSizeLevel ?? DEFAULT_TRANSPORT_SIZE_LEVEL,
      ),
      transportPosition: parseTransportPosition(parsed.transportPosition),
      cameraLockEnabled: parsed.cameraLockEnabled !== false,
      autoZoomEnabled: parsed.autoZoomEnabled !== false,
      boardModelDebug: parsed.boardModelDebug === true,
      journey: parsed.journey === "universe" ? "universe" : "book",
      spanId: typeof parsed.spanId === "string" ? parsed.spanId : null,
      repeatEnabled: parsed.repeatEnabled === true,
      optionsOpen: parsed.optionsOpen === true,
      spoilerLevel: readSpoilerLevel(parsed),
    };
  } catch {
    // ignore corrupt storage
  }
  return DEFAULT_PREFERENCES;
}

export function writeTimelineUiPreferences(
  prefs: TimelineUiPreferences,
): void {
  if (typeof localStorage === "undefined") {
    return;
  }
  localStorage.setItem(
    TIMELINE_UI_STORAGE_KEY,
    JSON.stringify({
      transportSizeLevel: clampTransportSizeLevel(prefs.transportSizeLevel),
      transportPosition: clampTransportPosition(prefs.transportPosition),
      cameraLockEnabled: prefs.cameraLockEnabled,
      autoZoomEnabled: prefs.autoZoomEnabled,
      boardModelDebug: prefs.boardModelDebug,
      journey: prefs.journey,
      spanId: prefs.spanId,
      repeatEnabled: prefs.repeatEnabled,
      optionsOpen: prefs.optionsOpen,
      spoilerLevel: prefs.spoilerLevel,
    }),
  );
}
